import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import {
  filterPeopleDirectory,
  sortPeopleDirectory,
  type PeopleDirectoryRow,
  type PeopleDirectorySortKey,
  type PeopleDirectoryStatusFilter,
} from "./peopleDirectoryModel";

const STATUS_OPTIONS: { value: PeopleDirectoryStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const COLUMNS: HubDataTableColumn<PeopleDirectoryRow, PeopleDirectorySortKey>[] = [
  {
    id: "name",
    header: "Name",
    sortKey: "name",
    sortValue: (row) => row.fullName,
    cell: (row) => (
      <Link className="text-brand-700 hover:underline" to={`/people/staff/${row.staffId}`}>
        {row.fullName}
      </Link>
    ),
  },
  {
    id: "title",
    header: "Title",
    sortKey: "title",
    sortValue: (row) => row.jobTitle ?? "",
    cell: (row) => row.jobTitle ?? "—",
  },
  {
    id: "department",
    header: "Department",
    sortKey: "department",
    sortValue: (row) => row.department ?? "",
    cell: (row) => row.department ?? "—",
  },
  {
    id: "manager",
    header: "Manager",
    sortKey: "manager",
    sortValue: (row) => row.managerName ?? "",
    cell: (row) => row.managerName ?? "—",
  },
  {
    id: "hired",
    header: "Hired",
    sortKey: "hired",
    overflow: "nowrap",
    sortValue: (row) => row.hiredOn ?? "",
    cell: (row) => row.hiredOn ?? "—",
  },
  {
    id: "terminated",
    header: "Terminated",
    sortKey: "terminated",
    overflow: "nowrap",
    sortValue: (row) => row.terminatedOn ?? "",
    cell: (row) => row.terminatedOn ?? "—",
  },
  {
    id: "openApps",
    header: "Open apps",
    sortKey: "openApps",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.openGrantCount,
    cell: (row) => row.openGrantCount,
  },
  {
    id: "active",
    header: "Active",
    sortKey: "active",
    overflow: "nowrap",
    sortValue: (row) => (row.active ? 1 : 0),
    cell: (row) => (row.active ? "Yes" : "No"),
  },
];

export function PeopleDirectoryPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<PeopleDirectoryStatusFilter>("active");
  const [sortKey, setSortKey] = useState<PeopleDirectorySortKey>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const directoryQuery = useQuery({
    queryKey: ["people", "directory"],
    queryFn: () => apiFetch<{ rows: PeopleDirectoryRow[] }>("/api/people/directory"),
  });

  const rows = directoryQuery.data?.rows ?? [];
  const visible = useMemo(() => {
    const filtered = filterPeopleDirectory(rows, search, status);
    return sortPeopleDirectory(filtered, sortKey, sortDir);
  }, [rows, search, status, sortKey, sortDir]);

  function onSort(key: PeopleDirectorySortKey) {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  const filtersActive = search.trim() !== "" || status !== "all";

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Staff records with job title, department, manager, hire date, and termination date. Open apps is the number of logins still to remove if this person leaves. Hub roles stay on Admin, Hub accounts.">
        Directory
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label htmlFor="people-directory-search">Search directory</Label>
            <Input
              id="people-directory-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by name, email, title, department, or manager..."
              className="mt-1"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={status === option.value ? "primary" : "secondary"}
                onClick={() => setStatus(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          directoryQuery.isLoading ? "People" : `Showing ${visible.length} of ${rows.length} people`
        }
        columns={COLUMNS}
        rows={visible}
        rowKey={(row) => row.staffId}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        loading={directoryQuery.isLoading}
        error={directoryQuery.isError ? (directoryQuery.error as Error).message : null}
        empty="No people."
        isFiltered={filtersActive}
        filteredEmpty="No people match these filters."
      />
    </div>
  );
}
